import { supabase } from '../lib/supabase';

export interface RateLimitState {
  isBlocked: boolean;
  blockedUntil?: string;
  attempts: number;
  remainingAttempts: number;
  blockDurationMinutes: number;
  isGloballyBlocked: boolean;
}

export interface RateLimitConfig {
  block_duration_minutes: number;
  max_attempts: number;
  max_blocked_notes_threshold: number;
}

export async function getRateLimitState(shortId: string): Promise<RateLimitState> {
  try {
    console.log('Getting rate limit state for note:', shortId);
    
    if (!shortId) {
      throw new Error('Note ID is required for rate limiting');
    }

    // First get the note's UUID
    const { data: note, error: noteError } = await supabase
      .from('notes')
      .select('id')
      .eq('short_id', shortId)
      .single();

    if (noteError || !note) {
      console.error('Failed to get note:', noteError);
      throw new Error('Failed to verify access status');
    }
    
    // Get client IP and rate limit config
    const { data, error } = await supabase.rpc('get_client_ip');
    
    if (error) {
      console.error('Failed to get client IP:', error);
      throw new Error('Failed to verify access status');
    }

    if (!data?.user_ip) {
      console.warn('No user IP returned');
      throw new Error('Could not determine client IP');
    }

    const config = data.rate_limit_config as RateLimitConfig;
    const blockDurationMinutes = config?.block_duration_minutes ?? 5;
    const maxAttempts = config?.max_attempts ?? 5;

    console.log('Fetching rate limit state for IP and note:', data.user_ip, note.id);

    const { data: rateLimits, error: rateLimitsError } = await supabase
      .from('rate_limits')
      .select()
      .eq('ip', data.user_ip)
      .eq('note_id', note.id)
      .eq('action_type', 'note_access')
      .maybeSingle();

    if (rateLimitsError) {
      console.error('Failed to get rate limits:', rateLimitsError);
      throw new Error('Failed to verify access status');
    }

    // If no rate limit record exists, return default unblocked state
    if (!rateLimits) {
      console.log('No rate limit record found, returning default state');
      return {
        isBlocked: false,
        attempts: 0,
        remainingAttempts: maxAttempts,
        blockDurationMinutes,
        isGloballyBlocked: false
      };
    }

    console.log('Found rate limit record:', rateLimits);

    // Check if block has expired
    if (rateLimits.blocked && rateLimits.blocked_until) {
      const blockExpired = new Date(rateLimits.blocked_until) <= new Date();
      
      // If block has expired, return unblocked state
      if (blockExpired) {
        console.log('Block has expired, returning unblocked state');
        return {
          isBlocked: false,
          attempts: 0,
          remainingAttempts: maxAttempts,
          blockDurationMinutes,
          isGloballyBlocked: false
        };
      }

      // Block is still active
      console.log('User is blocked until:', rateLimits.blocked_until);
      return {
        isBlocked: true,
        blockedUntil: rateLimits.blocked_until,
        attempts: rateLimits.attempts,
        remainingAttempts: Math.max(0, maxAttempts - rateLimits.attempts),
        blockDurationMinutes,
        isGloballyBlocked: false
      };
    }

    // User is not blocked
    return {
      isBlocked: false,
      attempts: rateLimits.attempts,
      remainingAttempts: Math.max(0, maxAttempts - rateLimits.attempts),
      blockDurationMinutes,
      isGloballyBlocked: false
    };
  } catch (error) {
    console.error('Failed to get rate limit state:', error);
    throw error;
  }
}

export async function checkRateLimit(shortId: string): Promise<void> {
  if (!shortId) {
    throw new Error('Note ID is required for rate limiting');
  }
  
  const state = await getRateLimitState(shortId);
  if (state.isGloballyBlocked) {
    throw new Error('Access is currently blocked due to multiple failed attempts across different notes.');
  }
  if (state.isBlocked) {
    throw new Error(`Access blocked. Please try again in ${state.blockDurationMinutes} minutes.`);
  }
}